// WatchParty browser transport. Wire-compatible with the desktop plugin.
const listeners = new Set();
const key = 'watchparty-session';
let saved;
try { saved = JSON.parse(sessionStorage.getItem(key)); } catch (_) { /* fresh session */ }
const client = {
    id: saved?.id || crypto.randomUUID(), config: saved?.config, party: null,
    status: 'Not in a party', messages: [], socket: null, timer: null, stopped: true,
    player: null, suppressUntil: 0, sequence: 0, pendingState: null,
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    notify() { listeners.forEach(fn => fn()); },
    persist() { sessionStorage.setItem(key, JSON.stringify({ id: this.id, config: this.config })); },
    send(message) { if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(message); },
    start(config) { this.leave(false); this.config = config; this.stopped = false; this.persist(); this.connect(); },
    connect() {
        if (this.stopped || !this.config) return;
        const c = this.config;
        const fields = c.code ? ['j', '1', c.name, c.code, c.password || '', this.id, c.token || '']
            : ['c', '1', c.name, c.password || '', c.title || 'Movie night', c.multi ? '1' : '0', this.id];
        this.status = c.code ? 'Joining your party…' : 'Creating your party…'; this.notify();
        const socket = new WebSocket(`${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/sync`, fields.map(encodeURIComponent).join('#'));
        this.socket = socket;
        socket.onmessage = ({ data }) => {
            if (this.socket !== socket) return;
            if (data === 'ping') return this.send('pong');
            if (data === 'badroom' || data === 'upgrade') {
                this.stopped = true; this.config = null; this.party = null; this.pendingState = null; this.persist();
                this.status = data === 'badroom' ? 'Party unavailable. It may have ended, or the password is incorrect.' : 'Please reload to update WatchParty.';
                this.notify(); return;
            }
            try {
                if (data.startsWith('party:')) {
                    this.party = JSON.parse(data.slice(6));
                    this.config = { ...c, code: this.party.code, token: this.party.inviteToken };
                    this.persist(); this.status = 'Connected'; this.notify();
                } else if (data.startsWith('msg:')) {
                    const end = data.indexOf(':', 4);
                    const member = this.party?.members.find(m => m.userId === data.slice(4, end));
                    this.messages = [...this.messages.slice(-99), { name: member?.userName || 'Friend', text: data.slice(end + 1) }]; this.notify();
                } else if (data.startsWith('cmd:')) {
                    const match = /^cmd:(\d+):([^:]+):(.*)$/s.exec(data);
                    if (match) this.receive(Number(match[1]), match[2], JSON.parse(match[3]));
                }
            } catch (_) { /* Ignore malformed or older unsupported packets. */ }
        };
        socket.onclose = () => {
            if (this.socket !== socket || this.stopped) return;
            this.status = 'Reconnecting… Your spot is held for 60 seconds.'; this.notify();
            this.timer = setTimeout(() => this.connect(), 1500);
        };
        socket.onerror = () => socket.close();
    },
    leave(clear = true) {
        this.stopped = true; clearTimeout(this.timer);
        if (clear) this.send('leave');
        const socket = this.socket; this.socket = null; socket?.close();
        this.party = null; this.pendingState = null; this.messages = [];
        if (clear) { this.config = null; this.persist(); }
        this.status = 'Not in a party'; this.notify();
    },
    isHost() { return !!this.party?.members.find(m => m.userId === this.id)?.isHost; },
    isLeader() { return this.party?.leaderId === this.id; },
    broadcast(command, data) {
        if (!this.isHost() || Date.now() < this.suppressUntil) return;
        this.send(`cmd:${command}:${JSON.stringify(data)}`);
    },
    receive(latency, command, data) {
        if (command === 'go' && Array.isArray(data) && data[0] === 'player' && data[1]) {
            this.player = null;
            this.suppressUntil = Date.now() + 2000;
            this.navigateMedia?.(data[1]).catch(() => { this.status = 'Unable to open the host’s stream. Choose a playable stream in Stremio.'; this.notify(); });
        } else if (command === 'goback') {
            this.suppressUntil = Date.now() + 1500; location.hash = '/';
        } else if (command === 'webnav' && typeof data === 'string' && /^\/player\//.test(data)) {
            if (location.hash.slice(1) !== data) { this.suppressUntil = Date.now() + 1500; location.hash = data; }
        } else if (command === 'state' && Number.isFinite(data?.time) && typeof data.paused === 'boolean') {
            this.pendingState = { data, latency, received: Date.now() }; this.applyState();
        }
    },
    applyState() {
        if (!this.player || !this.pendingState || !Number.isFinite(this.player.state.time)) return;
        const { data, latency, received } = this.pendingState;
        this.pendingState = null;
        const rate = Number.isFinite(data.playbackSpeed) ? Math.min(4, Math.max(0.25, data.playbackSpeed)) : 1;
        const target = data.time + (data.paused ? 0 : (latency + Date.now() - received) * rate);
        this.suppressUntil = Date.now() + 1200;
        if (Math.abs(this.player.state.time - target) > 2000 + latency) this.player.setTime(target);
        if (this.player.state.playbackSpeed !== rate) this.player.setPlaybackSpeed(rate);
        if (this.player.state.paused !== data.paused) this.player.setPaused(data.paused);
    },
    publishState() {
        const s = this.player?.state;
        if (!s || !Number.isFinite(s.time) || typeof s.paused !== 'boolean') return;
        this.broadcast('state', { time: s.time, paused: s.paused, playbackSpeed: s.playbackSpeed || 1, force: true,
            __wp: { seq: ++this.sequence, sentAt: Date.now(), source: this.id } });
    },
    invite() {
        return `${location.origin}/#/?party=${this.party.code}&invite=${this.party.inviteToken}`;
    }
};
module.exports = client;
